import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  TextInputV2,
  ButtonV2,
  SelectV2,
  LabraToolTipV2,
  NoteV2,
} from '@labrav/react-components'
import React, { useMemo, useState } from 'react'

import { faInputText, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { Grid, Typography } from '@mui/material'
import { useStyles } from './FlyOutProductFormFields.styles'
import {
  FlyoutProductBuyerFormFields,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import { FieldArray, FormikProps, useFormikContext } from 'formik'
import { get, isEmpty } from 'lodash'
import { getRandom } from '../../../common/utils/helperFunctions'
import {
  dataType,
  requiredField,
  validationType,
} from '../../../common/utils/OnboardingDataSelect'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import clsx from 'clsx'
import { FormLockedType } from '../../../types/FormLockedType'
import { FlyoutProductFormFieldsFaqs } from './FlyoutProductFormFieldsFaqs'

type FlyOutProductFormFieldsProps = {
  detailView?: boolean
  detailViewTooltip?: string
  handleRefLinkClick?: (inex: number) => void
}

export const FlyOutProductFormFields: React.FC<
  FormLockedType & FlyOutProductFormFieldsProps
> = ({ isFormLocked, detailView, detailViewTooltip, handleRefLinkClick }) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleBlur, setFieldValue, handleChange } =
    useFormikContext() as FormikProps<FlyoutProductSections>

  const buyerFormFieldsErrors = errors?.infoCollectionFormMetaData
  const buyerFormFieldsTouched = touched?.infoCollectionFormMetaData
  const infoCollectionFormMetaData = values.infoCollectionFormMetaData

  const customFields: FlyoutProductBuyerFormFields[] =
    infoCollectionFormMetaData.buyerFormFields || []

  const randomKey = useMemo(() => getRandom(), [customFields.length])

  const handleValidationTypeChange = (index: number, value: string) => {
    setFieldValue(
      `infoCollectionFormMetaData.buyerFormFields[${index}].validationType`,
      value === '' ? null : value
    )
  }

  const handleDataTypeChange = (index: number, value: string) => {
    setFieldValue(
      `infoCollectionFormMetaData.buyerFormFields[${index}].dataType`,
      value
    )
  }

  const handleIsRequiredChange = (index: number, value: string) => {
    setFieldValue(
      `infoCollectionFormMetaData.buyerFormFields[${index}].isRequired`,
      value === 'Yes' ? true : false
    )
  }

  const [indexModal, setIndexModal] = useState(0)

  return (
    <BoxV2
      heading={'Form fields'}
      headingIcon={
        <FontAwesomeIcon
          icon={faInputText}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <FlyoutProductFormFieldsFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
      isOptional={true}
    >
      <div
        className={classes.sectionContainer}
        data-testid="formfield-container"
      >
        <Typography
          className={classes.sectionText}
          data-testid="formfield-section-text"
        >
          In this section, you can add custom fields into the form on the buyer
          registration page. The buyer registration page already includes
          mandatory fields - First name, Company name, and Email address which
          are required so Labra can notify your buyers on different events.{' '}
          <ButtonV2
            styleType="link"
            onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
          >
            (Refer no. 6 in image no. 1)
          </ButtonV2>
        </Typography>
        <div
          className={classes.noteTextContainer}
          data-testid={'note-text-bold'}
        >
          <NoteV2
            description={
              "This data will be shared with you via email and be visible in Labra's UI whenever a buyer subscribes to your product"
            }
          />
        </div>
        <LabraToolTipV2
          followCursor={detailView}
          tooltipPlacement="bottom-start"
          description={detailViewTooltip}
          className={classes.fullWidth}
        >
          <div className={classes.customFieldContainer}>
            <FieldArray name="infoCollectionFormMetaData.buyerFormFields">
              {({ push, remove }) => (
                <>
                  {customFields.map(
                    (customfield, index) =>
                      customFields && (
                        <div
                          className={classes.row}
                          data-testid="customField-row"
                        >
                          <div className={classes.titleAndIcon}>
                            <div className={classes.title}>
                              Custom field {index + 1}
                            </div>
                            {!customfield?.isExisting && (
                              <div
                                className={clsx(
                                  classes.icon,
                                  detailView ? classes.disableTrash : ''
                                )}
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  style={{ width: '18px', height: '20px' }}
                                  data-testid="trash-customFields"
                                  onClick={() => {
                                    remove(index)
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          <Grid container spacing={3} width={'100%'}>
                            <Grid size={6}>
                              <div
                                key={`customFields-${randomKey}-title`}
                                className={classes.customFieldTitle}
                              >
                                <TextInputV2
                                  keyName={`infoCollectionFormMetaData.buyerFormFields.${index}.title`}
                                  title="Field name"
                                  type="text"
                                  placeHolder="Field name"
                                  maxCharacters={72}
                                  value={customfield?.title}
                                  isError={
                                    !!(
                                      get(
                                        buyerFormFieldsTouched,
                                        `buyerFormFields.${[index]}.title`,
                                        false
                                      ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.title`,
                                        false
                                      )
                                    )
                                  }
                                  onChange={handleChange}
                                  isReadOnly={isFormLocked as boolean}
                                  isRequired={false}
                                  errorText={
                                    (!!get(
                                      buyerFormFieldsTouched,
                                      `buyerFormFields.${[index]}.title`,
                                      false
                                    ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.title`,
                                        ''
                                      )) ||
                                    ''
                                  }
                                  showSpinner={false}
                                  onBlur={handleBlur}
                                  updatedValue={customfield?.title}
                                  viewOnly={detailView}
                                />
                              </div>
                            </Grid>
                            <Grid size={6}>
                              <div
                                key={`customFields-${randomKey}-dataType`}
                                className={classes.customFieldTitle}
                              >
                                <SelectV2
                                  keyName={`infoCollectionFormMetaData.buyerFormFields.${index}.dataType`}
                                  field={{
                                    title: 'Select field type',
                                    placeHolder: 'Select field type',
                                    id: 'customFields.dataType',
                                    isRequired: false,
                                    humanReadableKey: 'selectDataType',
                                    isReadOnly: isFormLocked,
                                    isCustom: false,
                                    defaultValue: customfield?.dataType,

                                    dataType: 'enum' as DataType,
                                    options: dataType,
                                  }}
                                  value={customfield?.dataType}
                                  viewOnly={detailView}
                                  onChange={e =>
                                    handleDataTypeChange(
                                      index,
                                      e.target.value as string
                                    )
                                  }
                                  onBlur={handleBlur}
                                  isError={
                                    !!(
                                      get(
                                        buyerFormFieldsTouched,
                                        `buyerFormFields.${[index]}.dataType`,
                                        false
                                      ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.dataType`,
                                        false
                                      )
                                    )
                                  }
                                  errorText={
                                    (!!get(
                                      buyerFormFieldsTouched,
                                      `buyerFormFields.${[index]}.dataType`,
                                      false
                                    ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.dataType`,
                                        ''
                                      )) ||
                                    ''
                                  }
                                />
                              </div>
                            </Grid>
                          </Grid>

                          <Grid container spacing={3} width={'100%'}>
                            <Grid size={6}>
                              <div
                                key={`customFields-${randomKey}-isRequired`}
                                className={classes.customField}
                              >
                                <SelectV2
                                  keyName={`infoCollectionFormMetaData.buyerFormFields.${index}.isRequired`}
                                  field={{
                                    title: 'Is required?',
                                    placeHolder: 'Is required?',
                                    id: 'customFields.isRequired',
                                    isRequired: false,
                                    humanReadableKey: 'isRequired',
                                    isReadOnly: isFormLocked,
                                    isCustom: false,
                                    defaultValue: customfield.isRequired
                                      ? 'Yes'
                                      : 'No',
                                    dataType: 'enum' as DataType,
                                    options: requiredField,
                                  }}
                                  value={customfield.isRequired ? 'Yes' : 'No'}
                                  viewOnly={detailView}
                                  onChange={e =>
                                    handleIsRequiredChange(
                                      index,
                                      e.target.value as string
                                    )
                                  }
                                  onBlur={handleBlur}
                                  isError={
                                    !!(
                                      get(
                                        buyerFormFieldsTouched,
                                        `buyerFormFields.${[index]}.isRequired`,
                                        false
                                      ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.isRequired`,
                                        false
                                      )
                                    )
                                  }
                                  errorText={
                                    (!!get(
                                      buyerFormFieldsTouched,
                                      `buyerFormFields.${[index]}.isRequired`,
                                      false
                                    ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.isRequired`,
                                        ''
                                      )) ||
                                    ''
                                  }
                                />
                              </div>
                            </Grid>
                            <Grid size={6}>
                              <div
                                key={`customFields-${randomKey}-validationType`}
                                className={classes.customField}
                              >
                                <SelectV2
                                  keyName={`infoCollectionFormMetaData.buyerFormFields.${index}.validationType`}
                                  field={{
                                    title: 'Validation type (Optional)',
                                    placeHolder: 'Validation type (Optional)',
                                    id: 'customFields.validationType',
                                    isRequired: false,
                                    humanReadableKey: 'validationType',
                                    isReadOnly: isFormLocked,
                                    isCustom: false,
                                    defaultValue: isEmpty(
                                      customfield.validationType
                                    )
                                      ? ''
                                      : customfield?.validationType,

                                    dataType: 'enum' as DataType,
                                    options: validationType,
                                  }}
                                  viewOnly={detailView}
                                  value={
                                    isEmpty(customfield.validationType)
                                      ? ''
                                      : customfield?.validationType
                                  }
                                  onChange={e =>
                                    handleValidationTypeChange(
                                      index,
                                      e.target.value as string
                                    )
                                  }
                                  onBlur={handleBlur}
                                  showNone
                                />
                              </div>
                            </Grid>
                          </Grid>

                          <Grid container spacing={3} width={'100%'}>
                            <Grid size={6}>
                              <div
                                key={`customFields-${randomKey}-minLength`}
                                className={classes.customField}
                              >
                                <TextInputV2
                                  keyName={`infoCollectionFormMetaData.buyerFormFields.${index}.minLength`}
                                  title="Minimum character length for the value"
                                  type="number"
                                  placeHolder="Enter Minimum character length for the value"
                                  minNumber={customfield.isRequired ? 1 : 0}
                                  value={customfield?.minLength}
                                  isError={
                                    !!(
                                      get(
                                        buyerFormFieldsTouched,
                                        `buyerFormFields.${[index]}.minLength`,
                                        false
                                      ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.minLength`,
                                        false
                                      )
                                    )
                                  }
                                  onChange={handleChange}
                                  isReadOnly={isFormLocked as boolean}
                                  viewOnly={detailView}
                                  isRequired={false}
                                  errorText={
                                    (!!get(
                                      buyerFormFieldsTouched,
                                      `buyerFormFields.${[index]}.minLength`,
                                      false
                                    ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.minLength`,
                                        ''
                                      )) ||
                                    ''
                                  }
                                  showSpinner={false}
                                  onBlur={handleBlur}
                                  supportingText="Pre-filled with default value"
                                  updatedValue={customfield?.minLength}
                                />
                              </div>
                            </Grid>
                            <Grid size={6}>
                              <div
                                key={`customFields-${randomKey}-maxLength`}
                                className={classes.customField}
                              >
                                <TextInputV2
                                  keyName={`infoCollectionFormMetaData.buyerFormFields.${index}.maxLength`}
                                  title="Maximum character length for the value"
                                  type="number"
                                  placeHolder="Enter a character length for the value"
                                  minNumber={Number(customfield?.minLength) + 1}
                                  value={customfield?.maxLength}
                                  isError={
                                    !!(
                                      get(
                                        buyerFormFieldsTouched,
                                        `buyerFormFields.${[index]}.maxLength`,
                                        false
                                      ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.maxLength`,
                                        false
                                      )
                                    )
                                  }
                                  onChange={handleChange}
                                  isReadOnly={isFormLocked as boolean}
                                  viewOnly={detailView}
                                  isRequired={false}
                                  errorText={
                                    (!!get(
                                      buyerFormFieldsTouched,
                                      `buyerFormFields.${[index]}.maxLength`,
                                      false
                                    ) &&
                                      get(
                                        buyerFormFieldsErrors,
                                        `buyerFormFields.${[index]}.maxLength`,
                                        ''
                                      )) ||
                                    ''
                                  }
                                  showSpinner={false}
                                  onBlur={handleBlur}
                                  supportingText="Pre-filled with default value"
                                  updatedValue={customfield?.maxLength}
                                />
                              </div>
                            </Grid>
                          </Grid>
                        </div>
                      )
                  )}
                  <div>
                    {' '}
                    <ButtonV2
                      styleType="link"
                      onClick={() =>
                        push({
                          title: '',
                          dataType: 'string',
                          isRequired: false,
                          validationType: null,
                          minLength: 1,
                          maxLength: 99,
                        })
                      }
                      data-testid="add-a-customField-button"
                      disabled={isFormLocked || detailView}
                    >
                      Add custom fields
                    </ButtonV2>
                  </div>
                </>
              )}
            </FieldArray>
          </div>
        </LabraToolTipV2>
      </div>
    </BoxV2>
  )
}
